"use client";

import { useState, useEffect } from 'react';

interface FloatingElement {
  id: string;
  x: number;
  y: number;
  size: number;
  delay: number;
  duration: number;
}

const floatingIcons: FloatingElement[] = [
  { id: 'icon-1', x: 8, y: 18, size: 44, delay: 0, duration: 9 },
  { id: 'icon-2', x: 88, y: 12, size: 36, delay: 1.2, duration: 11 },
  { id: 'icon-3', x: 18, y: 72, size: 40, delay: 2, duration: 8.5 },
  { id: 'icon-4', x: 78, y: 68, size: 48, delay: 0.8, duration: 10 },
  { id: 'icon-5', x: 52, y: 82, size: 32, delay: 1.5, duration: 7.5 },
  { id: 'icon-6', x: 92, y: 45, size: 28, delay: 2.5, duration: 9.5 },
];

const dataPoints: FloatingElement[] = [
  { id: 'dp-1', x: 14, y: 32, size: 9, delay: 0, duration: 3.2 },
  { id: 'dp-2', x: 82, y: 28, size: 7, delay: 0.6, duration: 3.8 },
  { id: 'dp-3', x: 28, y: 58, size: 8, delay: 1.1, duration: 4.1 },
  { id: 'dp-4', x: 68, y: 52, size: 10, delay: 1.7, duration: 3.5 },
  { id: 'dp-5', x: 44, y: 78, size: 7, delay: 2.2, duration: 3.9 },
  { id: 'dp-6', x: 60, y: 22, size: 8, delay: 0.4, duration: 4.3 },
];

// Retail icon SVGs
function ShoppingBagIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 50 50" fill="none">
      <rect x="10" y="18" width="30" height="24" rx="3" fill="#C5A572" opacity="0.25" stroke="#C5A572" strokeWidth="1.5" strokeOpacity="0.5" />
      <path d="M18 18 C18 12 32 12 32 18" stroke="#C5A572" strokeWidth="1.5" fill="none" strokeOpacity="0.6" />
      <line x1="20" y1="26" x2="30" y2="26" stroke="#C5A572" strokeWidth="1" strokeOpacity="0.4" />
    </svg>
  );
}

function StoreIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 50 50" fill="none">
      <rect x="8" y="22" width="34" height="22" rx="2" fill="#C5A572" opacity="0.2" stroke="#C5A572" strokeWidth="1.5" strokeOpacity="0.5" />
      <path d="M6 22 L12 10 L38 10 L44 22" stroke="#C5A572" strokeWidth="1.5" fill="none" strokeOpacity="0.5" />
      <rect x="20" y="30" width="10" height="14" rx="1" fill="#C5A572" opacity="0.3" />
      <rect x="12" y="28" width="7" height="7" rx="1" fill="#C5A572" opacity="0.25" />
      <rect x="31" y="28" width="7" height="7" rx="1" fill="#C5A572" opacity="0.25" />
    </svg>
  );
}

function ChartIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 50 50" fill="none">
      <rect x="8" y="8" width="34" height="28" rx="3" fill="#C5A572" opacity="0.15" stroke="#C5A572" strokeWidth="1.5" strokeOpacity="0.4" />
      <polyline points="14,30 20,22 28,26 36,16" stroke="#C5A572" strokeWidth="2" fill="none" strokeOpacity="0.7" />
      <circle cx="14" cy="30" r="2" fill="#C5A572" opacity="0.6" />
      <circle cx="20" cy="22" r="2" fill="#C5A572" opacity="0.6" />
      <circle cx="28" cy="26" r="2" fill="#C5A572" opacity="0.6" />
      <circle cx="36" cy="16" r="2" fill="#C5A572" opacity="0.6" />
      <line x1="8" y1="40" x2="42" y2="40" stroke="#C5A572" strokeWidth="1" strokeOpacity="0.3" />
    </svg>
  );
}

const iconComponents = [ShoppingBagIcon, StoreIcon, ChartIcon, ShoppingBagIcon, StoreIcon, ChartIcon];

export default function HeroSection() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 12,
        y: (e.clientY / window.innerHeight - 0.5) * 12,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section className="relative bg-navy pt-24 pb-14 px-4 min-h-[100svh] sm:pt-28 sm:pb-16 sm:px-6 md:pt-32 md:pb-20 md:min-h-screen flex items-center overflow-hidden">
      {/* Animated Background Gradients */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy to-[#0f1a2e]"></div>
        <div className="absolute top-1/3 right-1/4 w-[550px] h-[550px] bg-gold/6 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 left-1/5 w-[450px] h-[450px] bg-gold/8 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1.5s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-[300px] h-[300px] bg-gold/4 rounded-full blur-2xl animate-pulse-slow" style={{ animationDelay: '0.8s' }}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full min-w-0 max-w-5xl mx-auto text-center">
        {/* Minimal compliance motif — single horizontal rule + shield mark */}
        <div
          className="mb-8 sm:mb-10 flex items-center justify-center gap-4 sm:gap-5 max-w-md mx-auto"
          aria-hidden="true"
        >
          <span className="h-px flex-1 min-w-[2.5rem] bg-gradient-to-r from-transparent via-gold/25 to-gold/45" />
          <svg
            className="shrink-0 text-gold/75 w-7 h-7 sm:w-8 sm:h-8"
            viewBox="0 0 32 32"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.15"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M16 5.5l8.5 3.5v7.2c0 5.2-3.6 9.9-8.5 11.8-4.9-1.9-8.5-6.6-8.5-11.8V9L16 5.5z" />
            <path d="M11.5 15.5l3.2 3.2 6.3-6.3" />
          </svg>
          <span className="h-px flex-1 min-w-[2.5rem] bg-gradient-to-l from-transparent via-gold/25 to-gold/45" />
        </div>

        <h1 className="font-serif text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight sm:leading-[1.15] font-bold mb-8">
          Retail Data Chaos Solved with Real-Time{' '}
          <span
            className="italic"
            style={{
              color: '#C5A572',
              textShadow: '0 0 20px rgba(197, 165, 114, 0.4), 0 0 40px rgba(197, 165, 114, 0.2)',
            }}
          >
            Multi-Location Intelligence
          </span>
        </h1>
        <p className="text-white/90 text-base sm:text-lg md:text-xl leading-relaxed max-w-4xl mx-auto mb-12">
          Transform fragmented POS, inventory, and customer data into unified retail intelligence and operational excellence.
        </p>
        <button className="w-full max-w-sm sm:w-auto mx-auto sm:mx-0 bg-gold hover:bg-gold/90 text-navy font-bold text-base sm:text-lg px-6 sm:px-10 py-3.5 sm:py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105">
          Schedule Retail Assessment →
        </button>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          25% { transform: translateY(-18px) translateX(8px); }
          50% { transform: translateY(-10px) translateX(-8px); }
          75% { transform: translateY(-14px) translateX(4px); }
        }
        .animate-pulse-slow {
          animation: pulse 4.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
          opacity: 0;
        }
        @keyframes draw-line {
          from { stroke-dashoffset: 1000; }
          to { stroke-dashoffset: 0; }
        }
        .animate-draw-line {
          stroke-dasharray: 1000;
          stroke-dashoffset: 1000;
          animation: draw-line 3s ease-out forwards;
        }
      `}</style>
    </section>
  );
}